/**
 * The wines a member has brought to the club.
 *
 * One of the most-asked questions at a tasting, and the data (wines.brought_by)
 * has been there since the first migration with nowhere to see it.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Panel } from './Widgets';

interface BroughtWine {
  id: string;
  catalogId: string | null;
  wineName: string | null;
  producer: string | null;
  vintage: number | null;
  eventId: string;
  eventTitle: string;
  eventYear: number;
  groupAverage: number | null;
  groupRank: number | null;
}

export function WinesBrought({ memberId }: { memberId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['wines-brought', memberId],
    queryFn: () =>
      api.get<{ data: BroughtWine[] }>(`/members/${memberId}/wines`).then((r) => r.data),
  });

  if (isLoading || !data?.length) return null;

  return (
    <Panel title={`Wines brought (${data.length})`}>
      <ul className="divide-y divide-ink-800/60">
        {data.map((w) => (
          <li key={w.id} className="flex items-baseline justify-between gap-3 py-2">
            <div className="min-w-0">
              {/* Interlinking: the wine goes to the Cellar, the night to the archive. */}
              {w.catalogId ? (
                <Link
                  to={`/cellar/${w.catalogId}`}
                  className="block truncate text-sm text-cream-100 hover:text-gold-300"
                >
                  {w.wineName ?? 'Unnamed'}
                </Link>
              ) : (
                <span className="block truncate text-sm text-cream-100">
                  {w.wineName ?? 'Unnamed'}
                </span>
              )}
              <Link
                to={`/archive/${w.eventId}`}
                className="block truncate text-xs text-cream-300/50 hover:text-gold-400"
              >
                {[w.producer, w.vintage].filter(Boolean).join(' · ')}
                {w.producer || w.vintage ? ' — ' : ''}
                {w.eventTitle} ({w.eventYear})
              </Link>
            </div>
            <span className="shrink-0 text-right">
              {w.groupAverage != null ? (
                <span className="font-display text-gold-200">{w.groupAverage.toFixed(1)}</span>
              ) : null}
              {w.groupRank === 1 ? (
                <span className="ml-2 text-[10px] uppercase tracking-wide text-gold-400">
                  Won the night
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
